// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface NpsDistribution {
  promoters: number;
  passives: number;
  detractors: number;
}

interface SummaryData {
  totalResponses: number;
  avgNps: number | null;
  npsDistribution: NpsDistribution;
  avgFeatureRating: number | null;
  usefulRatio: number | null;
}

interface FeedbackSummaryProps {
  data: SummaryData | null;
  isLoading: boolean;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function Stars({ rating }: { rating: number }) {
  const full = Math.round(rating);
  return (
    <span className="text-amber-400 text-base leading-none" aria-label={`${rating} out of 5`}>
      {Array.from({ length: 5 }, (_, i) => (
        <span key={i}>{i < full ? '★' : '☆'}</span>
      ))}
    </span>
  );
}

// Skeleton card matching StatCard dimensions
function SkeletonCard() {
  return (
    <div className="bg-bg-surface rounded-xl border border-white/[0.06] px-4 py-3 animate-pulse">
      <div className="h-3 w-24 bg-white/[0.08] rounded mb-3" />
      <div className="h-7 w-16 bg-white/[0.08] rounded mb-2" />
      <div className="h-3 w-32 bg-white/[0.06] rounded" />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function FeedbackSummary({ data, isLoading }: FeedbackSummaryProps) {
  if (isLoading) {
    return (
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <SkeletonCard />
        <SkeletonCard />
        <SkeletonCard />
        <SkeletonCard />
      </div>
    );
  }

  const dist = data?.npsDistribution ?? { promoters: 0, passives: 0, detractors: 0 };

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
      {/* Total Responses */}
      <div className="bg-bg-surface rounded-xl border border-white/[0.06] px-4 py-3">
        <p className="text-xs text-gray-400 mb-1">Total Responses</p>
        <p className="text-2xl font-semibold text-white">
          {data?.totalResponses ?? 0}
        </p>
        <p className="text-xs text-gray-500 mt-0.5">across all types</p>
      </div>

      {/* Avg NPS */}
      <div className="bg-bg-surface rounded-xl border border-white/[0.06] px-4 py-3">
        <p className="text-xs text-gray-400 mb-1">Avg NPS</p>
        <p className="text-2xl font-semibold text-white">
          {data?.avgNps != null ? data.avgNps.toFixed(1) : '—'}
        </p>
        <p className="text-xs text-gray-500 mt-0.5">
          P {dist.promoters} / N {dist.passives} / D {dist.detractors}
        </p>
      </div>

      {/* Avg Rating */}
      <div className="bg-bg-surface rounded-xl border border-white/[0.06] px-4 py-3">
        <p className="text-xs text-gray-400 mb-1">Avg Rating</p>
        <div className="mt-1 mb-0.5">
          {data?.avgFeatureRating != null ? (
            <Stars rating={data.avgFeatureRating} />
          ) : (
            <span className="text-2xl font-semibold text-white">—</span>
          )}
        </div>
        <p className="text-xs text-gray-500 mt-0.5">feature ratings</p>
      </div>

      {/* Useful % */}
      <div className="bg-bg-surface rounded-xl border border-white/[0.06] px-4 py-3">
        <p className="text-xs text-gray-400 mb-1">Useful %</p>
        <p className="text-2xl font-semibold text-white">
          {data?.usefulRatio != null
            ? `${(data.usefulRatio * 100).toFixed(0)}%`
            : '—'}
        </p>
        <p className="text-xs text-gray-500 mt-0.5">AI content marked helpful</p>
      </div>
    </div>
  );
}
